export const MARKETING_ATTRIBUTION_STORAGE_KEY = 'ademero.marketingAttribution';

const MARKETING_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
  'gclid',
  'gbraid',
  'wbraid',
  'dclid',
  'msclkid',
  'fbclid',
  'ttclid',
  'li_fat_id',
  'gad_source',
  'gad_campaignid',
  '_gl',
  'am_session_id',
  'am_visitor_id',
  'am_handoff',
  'am_source_platform',
  'am_source_category',
  'am_source_evidence',
  'am_source_host',
];

const getDefaultStorage = () => {
  // Some browser privacy modes (e.g. Chrome/Edge "Block all cookies") make
  // the window.localStorage GETTER itself throw a SecurityError, so even
  // touching the property must be guarded. Default parameters are evaluated
  // before the function body, which is why this guard cannot live inside the
  // helpers' own try/catch blocks.
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (err) {
    return null;
  }
};

const getUrl = href => {
  try {
    return new URL(href, typeof window !== 'undefined' ? window.location.origin : undefined);
  } catch (err) {
    return null;
  }
};

const getMarketingParams = searchParams => {
  return MARKETING_PARAM_KEYS.reduce((params, key) => {
    const value = searchParams.get(key);
    if (value) {
      params[key] = value;
    }
    return params;
  }, {});
};

export const buildMarketingSearch = (search = '') => {
  const inputParams = new URLSearchParams(search || '');
  const outputParams = new URLSearchParams();

  MARKETING_PARAM_KEYS.forEach(key => {
    const value = inputParams.get(key);
    if (value) {
      outputParams.set(key, value);
    }
  });

  const output = outputParams.toString();
  return output ? `?${output}` : '';
};

export const buildLoginRedirectLocation = (location = {}) => {
  const search = buildMarketingSearch(location.search || '');
  const redirectLocation = {
    pathname: '/login',
    state: { from: location },
  };

  if (search) {
    redirectLocation.search = search;
  }

  return redirectLocation;
};

// PII hygiene: the /login/:uniqueID/:OTP deep-link route carries a one-time
// login code in the PATH itself, so any /login/... path is trimmed to plain
// /login before it is persisted anywhere. (React Router matches routes
// case-insensitively, hence the /i flag.)
const sanitizePathname = pathname =>
  /^\/login\/./i.test(pathname) ? '/login' : pathname;

// PII hygiene: keep origin + path plus only whitelisted marketing params.
// Non-marketing query values (tokens, emails, search terms, ...) are dropped
// before anything is persisted or pushed to the dataLayer.
const sanitizeLandingUrl = url =>
  url.origin + sanitizePathname(url.pathname) + buildMarketingSearch(url.search);

const sanitizeReferrer = referrer => {
  if (!referrer) {
    return '';
  }

  const url = getUrl(referrer);
  return url ? url.origin + sanitizePathname(url.pathname) : '';
};

export const captureMarketingAttribution = ({
  href,
  referrer = '',
  now = new Date().toISOString(),
} = {}) => {
  const url = getUrl(href);
  if (!url) {
    return null;
  }

  const params = getMarketingParams(url.searchParams);
  if (!Object.keys(params).length && !referrer) {
    return null;
  }

  return {
    capturedAt: now,
    landingUrl: sanitizeLandingUrl(url),
    referrer: sanitizeReferrer(referrer),
    params,
  };
};

export const readMarketingAttribution = (storage = getDefaultStorage()) => {
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(MARKETING_ATTRIBUTION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
};

export const writeMarketingAttribution = (
  storage = getDefaultStorage(),
  attribution,
) => {
  if (!storage || !attribution) {
    return;
  }

  try {
    storage.setItem(
      MARKETING_ATTRIBUTION_STORAGE_KEY,
      JSON.stringify(attribution),
    );
  } catch (err) {
    // Ignore storage failures (QuotaExceededError, SecurityError, ...).
    // Attribution is best-effort and must never break the login flow.
  }
};

export const clearMarketingAttribution = (storage = getDefaultStorage()) => {
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(MARKETING_ATTRIBUTION_STORAGE_KEY);
  } catch (err) {
    // Ignore storage failures (private mode / disabled storage).
  }
};

export const hasMarketingParams = attribution =>
  Boolean(
    attribution &&
      attribution.params &&
      Object.keys(attribution.params).length > 0,
  );

export const captureOrReadMarketingAttribution = ({
  href = typeof window !== 'undefined' ? window.location.href : '',
  referrer = typeof document !== 'undefined' ? document.referrer || '' : '',
  storage = getDefaultStorage(),
  now,
} = {}) => {
  const captured = captureMarketingAttribution({ href, referrer, now });

  // A genuinely new ad click (UTM / click-ID params in the URL) is a fresh
  // touch and overwrites whatever we stored before.
  if (hasMarketingParams(captured)) {
    writeMarketingAttribution(storage, captured);
    return captured;
  }

  // No marketing params on this visit. Never clobber an existing stored
  // attribution with a param-less return visit (e.g. the user clicked the ad
  // days ago and later came back directly) -- the original ad keeps the credit.
  const stored = readMarketingAttribution(storage);
  if (stored) {
    return stored;
  }

  // Nothing stored yet: allow a referrer-only first-touch capture to seed the
  // value (e.g. marketplace referrers that arrive without query params).
  if (captured) {
    writeMarketingAttribution(storage, captured);
  }
  return captured;
};

export const buildMarketingReferrer = ({
  fallbackReferrer = '',
  storage = getDefaultStorage(),
} = {}) => {
  const attribution = readMarketingAttribution(storage);
  return attribution?.landingUrl || sanitizeReferrer(fallbackReferrer);
};
